import React from 'react';
import {formatMoment, ReportSelectorFactory} from "./ReportSelectorFactory";
import {connect} from 'react-redux';
import {withBreadcrumbCurried} from "../Boilerplate/BreadcrumbContext";
import * as R from "ramda";
import {
  emailUpcomingBidDetailsReport,
  getBidStatusReport,
  getDailyLogDetailsReport,
  getMeetingMinutesDetailsReport,
  getProjectRfiDetailsReport,
  getProjectRfiStatusReport,
  getProjectStatusReport,
  getPunchStatusReport,
  getSubmittalDetailsReport,
  getSubmittalStatusReport,
  getTaskDetailsReport,
  getTaskStatusReport,
  getUpcomingBidDetailsReport
} from "../../actions/reports";
import StatusView from "./StatusView";
import UpcomingBidDetailsView from "./UpcomingBidDetailsView"
import {getProjects} from "../../actions/projects";
import DetailsView from "./DetailsView";
import {ReportPdfFactory} from "./ReportPdfFactory";
import moment from "moment";
import DailyLogDetailsView from "./DailyLogDetailsView";
import MeetingMinutesDetailsView from "./MeetingMinutesDetailsView";

const isSubdocument = (path) => ['task-status', 'task-details', 'rfi-status', 'rfi-details', 'submittal-status',
  'submittal-details', 'punchlist-status', 'daily-log-details', 'meeting-minutes-details'].includes(path);

const simpleSort = (a, b) => a > b ? 1 : a < b ? -1 : 0;

const columnsUpcomingBids = [
  {
    title: 'Bid Date',
    dataIndex: 'bidduedatetime',
    sorter: (a, b) => a.bidduedatetime - b.bidduedatetime,
    width: '20%',
    render: (timestamp) => <span className='formatted-text'>{formatMoment(timestamp)}</span>
  },
  {
    title: 'Pre-Bid Mandatory',
    dataIndex: 'prebidmeetingrequired',
    sorter: (a, b) => a.prebidmeetingrequired - b.prebidmeetingrequired,
    width: '10%',
    render: (mandatory) => mandatory ? <span className='color-secondary-1-3'>MANDATORY</span> : ''
  },
  {
    title: 'Pre-Bid Date',
    dataIndex: 'prebidmeetingdatetime',
    sorter: (a, b) => a.prebidmeetingdatetime - b.prebidmeetingdatetime,
    width: '20%',
    render: (timestamp) => <span className='formatted-text'>{formatMoment(timestamp)}</span>
  },
  {
    title: 'Bid Title',
    dataIndex: 'name',
    sorter: (a, b) => simpleSort(a.name.toLowerCase(), b.name.toLowerCase()),
    render: (text) => <span className='formatted-text'>{text}</span>
  },
  {
    title: 'Addendum',
    dataIndex: 'addendum',
    sorter: (a, b) => simpleSort(a.addendum, b.addendum),
    width: 100,
    render: (text) => <span className='formatted-text'>{text}</span>
  }
];

const columnsTaskDetails = [
  {
    title: 'Overview',
    dataIndex: 'overview',
    sorter: (a, b) => a.title > b.title,
    render: (text) => <span className='formatted-text'>{text}</span>
  },
  {
    title: 'Users',
    dataIndex: 'users',
    sorter: (a, b) => a.users > b.users,
    width: 200,
    render: (text) => <span className='formatted-text'>{text}</span>
  },
  {
    title: 'Status',
    dataIndex: 'status',
    sorter: (a, b) => a.status > b.status,
    width: 100,
    render: (text) => <span className='formatted-text'>{text}</span>
  }
];

const ColumnsFactory = (path) => {
  if (path === 'upcoming-bids') return columnsUpcomingBids;
  if (path === 'task-details') return columnsTaskDetails;
  return null;
};

const StateFactory = (path, title) => {
  const reportState = (state) => {
    if (path === 'bid-status') return state.reports.by_status.bids;
    if (path === 'upcoming-bids') return state.reports.details.upcomingbids;
    if (path === 'upcoming-bid-details') return state.reports.details.upcomingbids;
    if (path === 'project-status') return state.reports.by_status.projects;
    if (path === 'task-status') return state.reports.by_status.tasks;
    if (path === 'task-details') return state.reports.details.tasks;
    if (path === 'rfi-status') return state.reports.by_status.projectrfis;
    if (path === 'rfi-details') return state.reports.details.projectrfis;
    if (path === 'submittal-status') return state.reports.by_status.submittals;
    if (path === 'submittal-details') return state.reports.details.submittals;
    if (path === 'punchlist-status') return state.reports.by_status.punches;
    if (path === 'daily-log-details') return state.reports.details.dailylogs;
    if (path === 'meeting-minutes-details') return state.reports.details.meetingminutes;
    return null;
  };

  let table;
  if (path.includes('status')) table = path.split('-')[0];
  if (path === 'rfi-status') table = 'projectrfi';
  if (path === 'punchlist-status') table = 'punchlistitem';

  return (state, ownProps) => {
    if (isSubdocument(path)) {
      const projects = Object.values(state.view.data);
      if (!projects.length) return { report: { cache: { loading: true } } };
    }

    let state_props = {
      report: reportState(state) && ReportSelectorFactory(path)(state),
      projects: isSubdocument(path) ? reportState(state) && ReportSelectorFactory('projects')(state) : null,
      name: title,
      table,
      columns: ColumnsFactory(path),
    };

    if (path !== 'upcoming-bid-details')
      state_props['pdfGenerator'] = ReportPdfFactory(path);

    return state_props;
  }
};

const ReportControllerFactory = (dispatchAction, title, path) => {
  const real_path = `/reports/${path}`;

  // Default date filter: last 18 months
  const [from, to, filterby] = [moment().subtract(18, 'months').format(), moment().endOf('day').format(), 'datecreated'];

  let dispatch = null;
  let loadingDispatch = dispatch => {
    dispatch(dispatchAction({ from, to, filterby }))
  };

  if (isSubdocument(path)) {
    loadingDispatch = dispatch => {
      dispatch(getProjects());
      dispatch(dispatchAction({ from, to, filterby }))
    };
  }

  dispatch = (dispatch, ownProps) => {
    let dispatchProps = {
      onMount: () => loadingDispatch(dispatch),
      updateFilters: id => dispatch(dispatchAction({ id, from, to, filterby })),
    };
    if (path === 'upcoming-bid-details') dispatchProps['pdfGenerator'] = () => dispatch(emailUpcomingBidDetailsReport());
    return dispatchProps;
  };

  let view = [];
  view['daily-log-details'] = DailyLogDetailsView;
  view['meeting-minutes-details'] = MeetingMinutesDetailsView;
  view['upcoming-bid-details'] = UpcomingBidDetailsView;
  view['task-details'] = view['rfi-details'] = view['submittal-details'] = DetailsView;

  return R.compose(
    connect(StateFactory(path, title), dispatch),
    withBreadcrumbCurried({ title: title, path: real_path }),
  )(view[path] || StatusView);
};

export const BidStatus = ReportControllerFactory(getBidStatusReport, 'Bid Status', 'bid-status');
export const UpcomingBids = ReportControllerFactory(getUpcomingBidDetailsReport, 'Upcoming Bids', 'upcoming-bids');
export const UpcomingBidDetails = ReportControllerFactory(getUpcomingBidDetailsReport, 'Upcoming Bid Details', 'upcoming-bid-details');
export const ProjectStatus = ReportControllerFactory(getProjectStatusReport, 'Project Status', 'project-status');
export const TaskStatus = ReportControllerFactory(getTaskStatusReport, 'Task Status', 'task-status');
export const TaskDetails = ReportControllerFactory(getTaskDetailsReport, 'Task Details', 'task-details');
export const ProjectRfiStatus = ReportControllerFactory(getProjectRfiStatusReport, 'RFI Status', 'rfi-status');
export const ProjectRfiDetails = ReportControllerFactory(getProjectRfiDetailsReport, 'RFI Details', 'rfi-details');
export const SubmittalStatus = ReportControllerFactory(getSubmittalStatusReport, 'Submittal Status', 'submittal-status');
export const SubmittalDetails = ReportControllerFactory(getSubmittalDetailsReport, 'Submittal Details', 'submittal-details');
export const PunchlistStatus = ReportControllerFactory(getPunchStatusReport, 'Punch List Status', 'punchlist-status');
export const DailyLogDetails = ReportControllerFactory(getDailyLogDetailsReport, 'Daily Log Report', 'daily-log-details');
export const MeetingMinutesDetails = ReportControllerFactory(getMeetingMinutesDetailsReport, 'Meeting Minutes Report', 'meeting-minutes-details');



// WEBPACK FOOTER //
// ./src/components/Reports/ReportControllerFactory.js